/**
 * Application Insights Firebase Remote Config Utilities (Admin Frontend — Web only)
 *
 * Provides functions to fetch Application Insights configuration
 * from the locally cached Firebase Remote Config data (stored in localStorage),
 * with automatic fallback to DEFAULT_AI_CONFIG values.
 *
 * Firebase Remote Config must have been fetched and cached in localStorage
 * under the key 'remoteConfigData' before calling these functions.
 */

import {getApps, getApp, initializeApp} from '@firebase/app';
import {getRemoteConfig, fetchAndActivate, getAll} from '@firebase/remote-config';
import {DEFAULT_AI_CONFIG, parseAIRemoteConfig, logAIConfig, AIConfig} from './config';
import env from '../../env.json';

/** Storage key used by Firebase Remote Config caching */
const REMOTE_CONFIG_DATA_KEY = 'remoteConfigData';

/** Firebase Remote Config parameter key for Application Insights settings */
const APPLICATION_INSIGHTS_CONFIG_KEY = 'ApplicationInsights_Config';

/** Minimum time between Firebase Remote Config fetches (0 = always fetch fresh) */
const MINIMUM_FETCH_INTERVAL_MILLIS = 0;

/**
 * Fetches Firebase Remote Config from the backend and caches the raw parameter
 * values in localStorage under 'remoteConfigData', so getAIConfigFromRemote()
 * (and any other reader of the cache) can read them synchronously.
 *
 * Reuses the default Firebase app if one is already initialized (e.g. by
 * the analytics module) instead of creating a second instance.
 */
export const fetchAndCacheRemoteConfig = async (): Promise<void> => {
  try {
    const app = getApps().length ? getApp() : initializeApp((env as any).firebaseConfig);
    const remoteConfig = getRemoteConfig(app);
    remoteConfig.settings.minimumFetchIntervalMillis = MINIMUM_FETCH_INTERVAL_MILLIS;

    await fetchAndActivate(remoteConfig);

    const allValues = getAll(remoteConfig);
    localStorage.setItem(REMOTE_CONFIG_DATA_KEY, JSON.stringify(allValues));
  } catch (error) {
    console.error('[AppInsights] Error fetching Firebase Remote Config:', error);
  }
};

/**
 * Fetches Application Insights configuration from Firebase Remote Config.
 * Reads from localStorage where Firebase Remote Config data is cached.
 * Firebase Remote Config must have been fetched and cached before calling this.
 *
 * @returns Validated Application Insights configuration object
 */
export const getAIConfigFromRemote = async (): Promise<AIConfig> => {
  try {
    const remoteConfigString = localStorage.getItem(REMOTE_CONFIG_DATA_KEY);
    if (!remoteConfigString) {
      return DEFAULT_AI_CONFIG;
    }

    const remoteConfigData = JSON.parse(remoteConfigString);

    // Read ApplicationInsights_Config from environment-specific FcModules (e.g. QA1_FcModules)
    let aiRemoteConfigValue: string | undefined;

    const envFcModulesKey = `${(env as any).ENVIRONMENT}_FcModules`;
    const fcModulesValue = remoteConfigData[envFcModulesKey]?._value;
    if (fcModulesValue) {
      const fcModulesData = typeof fcModulesValue === 'string' ? JSON.parse(fcModulesValue) : fcModulesValue;
      const nestedConfig = fcModulesData[APPLICATION_INSIGHTS_CONFIG_KEY];
      if (nestedConfig) {
        aiRemoteConfigValue = typeof nestedConfig === 'string' ? nestedConfig : JSON.stringify(nestedConfig);
        console.log(`[AppInsights] Found config in ${envFcModulesKey}`);
      }
    }

    if (!aiRemoteConfigValue) {
      console.log(
        `[AppInsights] No '${APPLICATION_INSIGHTS_CONFIG_KEY}' key found in ${envFcModulesKey}, using defaults`,
      );
      return DEFAULT_AI_CONFIG;
    }

    const aiConfig = parseAIRemoteConfig(aiRemoteConfigValue);
    logAIConfig(aiConfig, `Firebase Remote Config (${envFcModulesKey})`);
    return aiConfig;
  } catch (error) {
    console.error('[AppInsights] Error fetching config from remote:', error);
    logAIConfig(DEFAULT_AI_CONFIG, 'Default (fallback)');
    return DEFAULT_AI_CONFIG;
  }
};

/**
 * Gets a single Application Insights config value by key.
 *
 * @param key - Config property name
 * @param defaultValue - Fallback if key not present
 * @returns Config value or defaultValue
 */
export const getAIConfigValue = async <K extends keyof AIConfig>(
  key: K,
  defaultValue: AIConfig[K],
): Promise<AIConfig[K]> => {
  try {
    const config = await getAIConfigFromRemote();
    return config[key] !== undefined ? config[key] : defaultValue;
  } catch (error) {
    console.error(`[AppInsights] Error getting config value for key '${key}':`, error);
    return defaultValue;
  }
};

/**
 * Returns true if Firebase Remote Config data is available in localStorage.
 *
 * @returns Whether cached remote config is available
 */
export const isRemoteConfigAvailable = (): boolean => {
  try {
    return Boolean(localStorage.getItem(REMOTE_CONFIG_DATA_KEY));
  } catch (error) {
    console.error('[AppInsights] Error checking remote config availability:', error);
    return false;
  }
};
